<template>
  <SheetModal
    ref="modalRef"
    class="PlayerModalView"
    fit="hug"
    startOffset="24px"
    variant="custom"
    data-theme="dark"
    :aria-label="player ? `Player details for ${player.name}` : 'Player details'"
    :aria-busy="!isPlayerLoaded"
  >
    <div
      class="PlayerModalView__layout"
    >
      <div class="PlayerModalView__header">
        <PlayerAvatar
          class="PlayerModalView__avatar"
          size="large"
          variant="secondary"
          :modelValue="player"
          :isPlayerLoading="!isPlayerLoaded"
          :isClickable="false"
          isHighlighted
        />
        <button
          v-if="player && currentPlayer?.id === player.id"
          class="PlayerModalView__editButton"
          data-testid="editButton"
          type="button"
          aria-label="Edit profile"
          @click="handleEditClick"
        >
          <Icon
            name="pencil"
          />
        </button>
        <h1
          class="PlayerModalView__title"
          data-testid="name"
        >
          <template v-if="player?.name">
            {{ player.name }}
          </template>
          <template v-else>
          &nbsp;
          </template>
        </h1>
        <p class="PlayerModalView__subtitle">
          <template v-if="formattedJerseyNumber || formattedPlayerPosition">
            <span
              v-if="formattedJerseyNumber"
              data-testid="jerseyNumber"
            >{{ formattedJerseyNumber }}</span>
            <span
              v-if="formattedPlayerPosition"
              data-testid="position"
            >{{ formattedPlayerPosition }}</span>
          </template>
          <template v-else>
          &nbsp;
          </template>
        </p>
        <address class="PlayerModalView__location">
          <Icon name="location" />
          <span
            class="PlayerModalView__locationLabel"
            data-testid="location"
          >
            <template v-if="player?.location">{{ player.location }}</template>
            <template v-else-if="player">Unknown</template>
            <template v-else>
          &nbsp;
            </template>
          </span>
        </address>
        <dl
          v-if="formattedPlayerHeight"
          class="PlayerModalView__measurementList"
        >
          <div
            v-if="formattedPlayerHeight"
            class="PlayerModalView__measurementItem"
          >
            <dt
              :id="`heightLabel-${player?.id}`"
              class="PlayerModalView__measurementItemLabel"
            >
              Height
            </dt>
            <dd
              class="PlayerModalView__measurementItemValue"
              :aria-labelledby="`heightLabel-${player?.id}`"
            >
              {{ formattedPlayerHeight }}
            </dd>
          </div>
          <div
            v-if="formattedPlayerWeight"
            class="PlayerModalView__measurementItem"
          >
            <dt
              :id="`weightLabel-${player?.id}`"
              class="PlayerModalView__measurementItemLabel"
            >
              Weight
            </dt>
            <dd
              class="PlayerModalView__measurementItemValue"
              :aria-labelledby="`weightLabel-${player?.id}`"
            >
              {{ formattedPlayerWeight }}
            </dd>
          </div>
        </dl>

        <FormButton
          v-if="player?.venmo_username"
          class="PlayerModalView__venmoButton"
          logoName="venmo"
          backgroundColor="#3396CD"
          textColor="#fff"
          @click="handleVenmoClick"
        >
          Send a Venmo request
        </FormButton>
      </div>
      <section
        class="PlayerModalView__content"
        data-theme="light"
      >
        <h2 class="PlayerModalView__statsTitle">
          Run stats
        </h2>
        <StatRow>
          <StatRowItem
            data-testid="runCount"
            :label="runCountLabel"
            :modelValue="runCount"
            :aria-label="`${runCount} ${runCountLabel}`"
          />
          <StatRowItem
            data-testid="daysSinceLastRun"
            :label="daysSinceLastRunLabel"
            :modelValue="daysSinceLastRun"
            :aria-label="`${daysSinceLastRun} ${daysSinceLastRunLabel}`"
          />
          <StatRowItem
            data-testid="weeklyStreakCount"
            :label="weeklyStreakCountLabel"
            :modelValue="weeklyStreakCount"
            :aria-label="`${weeklyStreakCount} ${weeklyStreakCountLabel}`"
          />
        </StatRow>
      </section>
    </div>
  </SheetModal>
</template>

<script setup lang="ts">
import FormButton from "@/components/FormButton.vue";
import Icon from "@/components/Icon.vue";
import PlayerAvatar from "@/components/PlayerAvatar.vue";
import SheetModal from "@/components/SheetModal.vue";
import StatRow from "@/components/StatRow.vue";
import StatRowItem from "@/components/StatRowItem.vue";
import useModalRoute from "@/composables/useModalRoute";
import useTheme from "@/composables/useTheme";
import { RouteName } from "@/lib/router/routes";
import detectOS from "@/lib/utils/platform/detectOS";
import { OS } from "@/lib/utils/platform/detectOS.types";
import haptic from "@/lib/utils/platform/haptic";
import formatHeight from "@/lib/utils/string/formatHeight";
import formatPlayerPosition from "@/lib/utils/string/formatPlayerPosition";
import useAuthStore from "@/stores/useAuthStore";
import useModalStore from "@/stores/useModalStore";
import usePlayerStore from "@/stores/usePlayerStore";
import { Theme } from "@shared/lib/constants/themes";
import { PublicPlayerProfile } from "@shared/lib/database/query.types";
import { differenceInCalendarDays, parseISO } from "date-fns";
import { storeToRefs } from "pinia";
import { computed, onBeforeMount, ref, useTemplateRef, watch } from "vue";

const {
  playerId,
} = defineProps<{
  playerId: string,
}>();

const {
  openModal,
} = useModalStore();
const modalRef = useTemplateRef("modalRef");

const {
  currentPlayer,
} = storeToRefs(useAuthStore());

const {
  getPlayer,
  fetchPlayer,
} = usePlayerStore();

const {
  isModalActive,
} = useModalRoute(RouteName.player);

const player = ref<PublicPlayerProfile | null>(null);
const isPlayerLoaded = ref(false);
const theme = computed(() => player.value?.theme as keyof typeof Theme | null | undefined);
useTheme(modalRef, theme);

onBeforeMount(async () => {
  const cachedPlayer = getPlayer(playerId);
  if (cachedPlayer) {
    player.value = {
      jersey_number: null,
      position: null,
      location: null,
      height: null,
      weight: null,
      venmo_username: null,
      run_count: 0,
      last_run_at: null,
      weekly_streak_count: 0,
      ...cachedPlayer,
    };
  }

  isPlayerLoaded.value = !!player.value;

  refetchPlayer();
});

const formattedJerseyNumber = computed(() => {
  return player.value?.jersey_number ? `#${player.value.jersey_number}` : "";
});

const formattedPlayerPosition = computed(() => {
  return player.value?.position ? formatPlayerPosition(player.value.position) : "";
});

const formattedPlayerHeight = computed(() => {
  return player.value?.height ? formatHeight(player.value.height) : null;
});

const formattedPlayerWeight = computed(() => {
  return player.value?.weight ? `${player.value.weight} lbs` : null;
});

const runCount = computed(() => player.value?.run_count ?? 0);

const daysSinceLastRun = computed(() => {
  if (!player.value?.last_run_at) return 0;
  return Math.max(0, differenceInCalendarDays(new Date(), parseISO(player.value.last_run_at)));
});

const weeklyStreakCount = computed(() => player.value?.weekly_streak_count ?? 0);

const runCountLabel = computed(() => `run${runCount.value === 1 ? "" : "s"} played`);
const daysSinceLastRunLabel = computed(() => `day${daysSinceLastRun.value === 1 ? "" : "s"} since last run`);
const weeklyStreakCountLabel = computed(() => `week${weeklyStreakCount.value === 1 ? "" : "s"} in a row`);

watch(isModalActive, (isActive) => {
  if (isActive && playerId === currentPlayer.value?.id) refetchPlayer();
});

async function refetchPlayer () {
  try {
    const _player = await fetchPlayer(playerId);
    if (_player) player.value = _player;

    isPlayerLoaded.value = true;
  } catch (error) {
    if (!player.value) alert((error as Error).message);
  }
}

function handleEditClick () {
  if (!player.value) return;

  haptic();

  openModal({
    name: RouteName.editProfile,
  });
}

function handleVenmoClick () {
  if (!player.value?.venmo_username) return;

  const os = detectOS();
  const note = "🏀";
  const escapedUsername = encodeURIComponent(player.value.venmo_username);

  if (os === OS.iOS) {
    window.location.href = `venmo://paycharge?recipients=${escapedUsername}&note=${note}`;
  } else if (os === OS.android) {
    window.location.href = `intent://paycharge?&recipients=${escapedUsername}&note=${note}#Intent;scheme=venmo;S.browser_fallback_url=https%3A%2F%2Fvenmo.com%2F${escapedUsername};end`;
  } else {
    window.open(`https://venmo.com/${escapedUsername}`, "_blank");
  }
}
</script>

<style lang="scss" scoped>
.PlayerModalView__header {
  display: grid;
  padding: 0 20px 24px;

  background-color: var(--themeColorPrimary);
  color: var(--themeColorPrimaryForeground);
}

.PlayerModalView__avatar {
  margin: calc(-1 * var(--avatarSize) / 2) 0 16px;
}

.PlayerModalView__editButton {
  position: absolute;
  top: 20px;
  right: 20px;

  color: var(--themeLightTransparent700);
}

.PlayerModalView__title {
  font-size: 32px;
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
}

.PlayerModalView__subtitle {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 8px;

  font-size: 20px;
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);

  span + span {
    &::before {
      margin-right: 8px;
      color: var(--themeLightTransparent700);
      content: "|";
    }
  }
}

.PlayerModalView__location {
  --iconSize: 16px;
  display: flex;
  gap: 6px;
  align-items: center;
  margin-top: 10px;

  color: var(--themeLightTransparent700);

  font-size: 18px;
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
}

.PlayerModalView__locationLabel {
  position: relative;
  top: max(1px, var(--fontSafariOffset));

  overflow: hidden;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.PlayerModalView__measurementList {
  display: flex;
  gap: 24px;
  align-items: center;
  margin-top: calc(12px + var(--fontSafariOffset));
}

.PlayerModalView__measurementItem {
  display: flex;
  gap: 8px;
  align-items: center;
}

.PlayerModalView__measurementItemLabel {
  color: var(--themeLightTransparent700);
  font-weight: var(--fontWeightMedium);
}

.PlayerModalView__measurementItemValue {
  font-family: var(--fontFamilyMono);
  font-weight: var(--fontWeightMedium);
}

.PlayerModalView__measurementItemValueUnit {
  font-family: var(--fontFamilyNormal);
}

.PlayerModalView__content {
  padding-bottom: calc(var(--safeAreaInsetBottom) + 24px);
  background-color: var(--themeColorSurface);
}

.PlayerModalView__statsTitle {
  display: flex;
  align-items: center;
  height: 44px;
  padding: 0 16px;

  background-color: var(--themeColorSecondary);
  color: var(--themeColorSecondaryForeground);

  font-size: 14px;
  font-weight: var(--fontWeightMedium);
  text-transform: uppercase;
}

.PlayerModalView__venmoButton {
  margin-top: 24px;
}
</style>
